import React, { useState } from 'react';
import { adjustStock, BranchStockItem } from '../../services/stockService';
import { X, AlertTriangle, Save } from 'lucide-react';

interface StockAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: BranchStockItem;
  branchId: number;
  onSuccess: () => void;
}

const StockAdjustmentModal: React.FC<StockAdjustmentModalProps> = ({
  isOpen,
  onClose,
  item,
  branchId,
  onSuccess,
}) => {
  const [newQty, setNewQty] = useState<number>(item.qty);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const qtyDifference = newQty - item.qty;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!notes.trim()) {
      setError('Notes wajib diisi untuk stock adjustment');
      return;
    }

    if (newQty === item.qty) {
      setError('Qty baru sama dengan qty saat ini');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await adjustStock(item.item_id, branchId, newQty, notes);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to adjust stock');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Stock Adjustment (Opname)</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Item Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">Item</div>
            <div className="font-semibold text-gray-900">{item.name}</div>
            <div className="text-sm text-gray-600">SKU: {item.sku}</div>
            <div className="text-sm text-gray-600">Category: {item.category}</div>
          </div>

          {/* Current Stock */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stok Saat Ini
            </label>
            <div className="text-2xl font-bold text-gray-900">
              {item.qty} {item.unit}
            </div>
          </div>

          {/* New Quantity */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Stok Baru (Hasil Opname) *
            </label>
            <input
              type="number"
              value={newQty}
              onChange={(e) => setNewQty(parseInt(e.target.value) || 0)}
              min="0"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Difference Display */}
          {qtyDifference !== 0 && (
            <div className={`mb-4 p-3 rounded-lg ${
              qtyDifference > 0 ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center">
                <AlertTriangle className={`w-5 h-5 mr-2 ${
                  qtyDifference > 0 ? 'text-green-600' : 'text-red-600'
                }`} />
                <div>
                  <div className="font-semibold text-sm">
                    {qtyDifference > 0 ? 'Penambahan' : 'Pengurangan'}:
                  </div>
                  <div className={`text-lg font-bold ${
                    qtyDifference > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {qtyDifference > 0 ? '+' : ''}{qtyDifference} {item.unit}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Keterangan (Wajib) *
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              required
              placeholder="Jelaskan alasan adjustment (misal: hasil stock opname, koreksi kesalahan input, dll)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Notes penting untuk audit trail
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Warning for Owner/Admin only */}
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <strong>Perhatian:</strong> Stock adjustment akan tercatat di audit log.
                Pastikan qty dan keterangan sudah benar.
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !notes.trim() || newQty === item.qty}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Adjustment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockAdjustmentModal;
